import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { RunStatus } from '../../../../../../backend/src/types/types';
import { AllRunStatuses, RunStatusLabels } from '../../../lib/pipeline-status';
import { formatDatetimeForTable } from '../../../lib/table-utils';
import { useUpdateRunStatus } from '@/api/mutations/useUpdateRunStatus';
import { useFetchAllActiveRuns } from '@/api/queries/analyticsQueries/useFetchAllActiveRuns';
import { ActiveRun } from '@/api/types/backend-dto-types';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const fallbackTableData: ActiveRun[] = [];

const columnHelper = createColumnHelper<ActiveRun>();

export function AllRuns() {
  usePlatformPermissionCheck(Permission.PlatformManageRuns);

  const { data: runsData } = useFetchAllActiveRuns();
  const { mutate: updateRunStatus, isLoading: isUpdatingStatus, variables } = useUpdateRunStatus();

  const [globalFilter, setGlobalFilter] = useState('');

  const updatingRunId = isUpdatingStatus ? variables?.runId : undefined;

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdDate', {
        header: () => <span>Start Time</span>,
        cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
      }),
      columnHelper.accessor('completedAtEstimate', {
        header: () => <span>Est. Completion</span>,
        cell: (info) => {
          const value = info.getValue();
          return <span className="whitespace-nowrap">{value ? formatDatetimeForTable(value) : '—'}</span>;
        },
      }),
      columnHelper.accessor('matter.organisation.name', {
        header: () => <span>Organisation</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('createdBy.email', {
        header: () => <span>User Email</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('totalPages', {
        header: () => <span>Pages</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('type', {
        header: () => <span>Type</span>,
        cell: (info) => <span className="capitalize">{info.getValue()}</span>,
      }),
      columnHelper.accessor('status', {
        header: () => <span>Status</span>,
        cell: (info) => {
          const status = info.getValue();
          const knownStatus = AllRunStatuses.find((option) => option === status);
          const runId = info.row.original.id;

          return (
            <Select
              value={knownStatus}
              disabled={updatingRunId === runId}
              onValueChange={(newStatus) => updateRunStatus({ runId, status: newStatus as RunStatus })}
            >
              <SelectTrigger className="-m-0.5 h-auto w-52 border-none p-1 pl-2 focus:outline-none">
                <SelectValue placeholder={status || '—'} />
              </SelectTrigger>
              <SelectContent>
                {AllRunStatuses.map((option) => (
                  <SelectItem key={option} value={option}>
                    {RunStatusLabels[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      }),
    ],
    [updateRunStatus, updatingRunId],
  );

  const table = useReactTable({
    data: runsData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Admin', active: false },
          { href: '/admin/runs', label: 'All Runs', active: true },
        ]}
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="All Runs" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable table={table} plural="runs" globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
    </>
  );
}
